import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { Router } from '@angular/router';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import {
  CreateRoleOnboardingCourse,
  CreateRoleOnboardingCourseDefaultValues,
  ROLE_ONBOARDING_VIDEO_MATERIAL_TYPE
} from 'src/app/components/role-onboarding/role-onboarding';
import { ApplicationRoles, ApplicationRolesEnumDescriptionService } from 'src/app/components/users/user-roles';
import { FileService } from 'src/app/services/file.service';
import { RoleOnboardingService } from 'src/app/services/role-onboarding.service';
import { ToastService } from 'src/app/services/toast-service.service';

@Component({
    selector: 'app-create-role-onboarding',
    templateUrl: './create-role-onboarding.component.html',
    styleUrls: ['./create-role-onboarding.component.scss'],
    standalone: false
})
export class CreateRoleOnboardingComponent {
  course: CreateRoleOnboardingCourse = CreateRoleOnboardingCourseDefaultValues();
  isSubmitted = false;
  courseStatus = 'active';
  pageName = CheckPageAccess.ADDROLEONBOARDING;
  roles: { id: ApplicationRoles; name: string }[] = [];
  videoSource: 'link' | 'upload' = 'link';
  uploadedFileName = '';
  isUploading = false;
  uploadProgress = 0;
  isSaving = false;

  constructor(
    private roleOnboardingService: RoleOnboardingService,
    private fileService: FileService,
    private toast: ToastService,
    private router: Router,
    private roleDescriptions: ApplicationRolesEnumDescriptionService) {
    this.roles = Object.keys(ApplicationRoles)
      .filter(key => isNaN(Number(key)))
      .map(key => {
        const id = ApplicationRoles[key as keyof typeof ApplicationRoles];
        return { id, name: this.roleDescriptions.getDescription(id) };
      });
  }

  onVideoSourceChange(source: 'link' | 'upload'): void {
    this.videoSource = source;
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadedFileName = file.name;

    this.fileService.uploadMaterialFile(formData, ROLE_ONBOARDING_VIDEO_MATERIAL_TYPE).subscribe({
      next: (httpEvent) => {
        if (httpEvent.type === HttpEventType.UploadProgress && httpEvent.total) {
          this.uploadProgress = Math.min(99, Math.round((100 * httpEvent.loaded) / httpEvent.total));
        } else if (httpEvent.type === HttpEventType.Response) {
          this.course.videoFileId = httpEvent.body.id;
          this.course.videoUrl = '';
          this.uploadedFileName = httpEvent.body.fileName || file.name;
          this.uploadProgress = 100;
          this.isUploading = false;
        }
      },
      error: () => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.course.videoFileId = null;
        this.uploadedFileName = '';
        this.toast.showErrorMessage('Video upload failed. Please try again.');
        input.value = '';
      }
    });
  }

  stepOrder(delta: number): void {
    this.course.displayOrder = Math.max(1, (Number(this.course.displayOrder) || 1) + delta);
  }

  normalizeOrder(): void {
    this.course.displayOrder = Math.max(1, Math.floor(Number(this.course.displayOrder)) || 1);
  }

  save(form: NgForm): void {
    this.isSubmitted = true;
    this.course.isActive = this.courseStatus === 'active';

    if (this.course.role === null || !this.course.title.trim()) {
      return;
    }

    if (this.isUploading || this.isSaving) {
      return;
    }

    if (this.videoSource === 'link') {
      if (!this.course.videoUrl.trim()) {
        return;
      }
      if (!this.isHttpsUrl(this.course.videoUrl.trim())) {
        this.toast.showErrorMessage('Video URL must be a valid https link');
        return;
      }
    } else if (!this.course.videoFileId) {
      this.toast.showErrorMessage('Please upload a video file');
      return;
    }

    const payload: CreateRoleOnboardingCourse = {
      role: this.course.role,
      title: this.course.title.trim(),
      description: this.course.description,
      isActive: this.course.isActive,
      isMandatory: this.course.isMandatory,
      displayOrder: Math.max(1, Math.floor(Number(this.course.displayOrder)) || 1),
      videoUrl: this.videoSource === 'link' ? this.course.videoUrl.trim() : '',
      videoFileId: this.videoSource === 'upload' ? this.course.videoFileId : null
    };

    this.isSaving = true;
    this.roleOnboardingService.create(payload).subscribe({
      next: (created) => {
        this.isSaving = false;
        if (created) {
          this.toast.showMessage('Role training created successfully');
          this.router.navigateByUrl('/role-training-list');
        } else {
          this.toast.showErrorMessage('Unable to create role training');
        }
      },
      error: () => {
        this.isSaving = false;
        this.toast.showErrorMessage('Unable to create role training');
      }
    });
  }

  private isHttpsUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
